<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import {
  IG_LIMITS,
  buildEmptyButton,
} from 'dashboard/helper/instagramTemplateHelper';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  // Generic-template cards may have no buttons at all; a button template
  // requires at least one.
  required: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const buttons = computed({
  get: () => props.modelValue || [],
  set: value => emit('update:modelValue', value),
});

const typeOptions = computed(() => [
  {
    value: 'web_url',
    label: t('MESSAGE_TEMPLATES.INSTAGRAM.BUTTONS.TYPES.WEB_URL'),
  },
  {
    value: 'postback',
    label: t('MESSAGE_TEMPLATES.INSTAGRAM.BUTTONS.TYPES.POSTBACK'),
  },
]);

const canAdd = computed(() => buttons.value.length < IG_LIMITS.MAX_BUTTONS);
const canRemove = computed(() => !props.required || buttons.value.length > 1);

const addButton = () => {
  buttons.value = [...buttons.value, buildEmptyButton()];
};

const removeButton = index => {
  buttons.value = buttons.value.filter((_, i) => i !== index);
};

const updateButton = (index, key, value) => {
  buttons.value = buttons.value.map((button, i) =>
    i === index ? { ...button, [key]: value } : button
  );
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between">
      <span class="text-sm font-medium text-n-slate-11">
        {{ t('MESSAGE_TEMPLATES.INSTAGRAM.BUTTONS.TITLE') }}
      </span>
      <span class="text-xs text-n-slate-10">
        {{ buttons.length }}/{{ IG_LIMITS.MAX_BUTTONS }}
      </span>
    </div>

    <div
      v-for="(button, index) in buttons"
      :key="index"
      class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak bg-n-solid-1"
    >
      <div class="flex items-start gap-2">
        <div class="w-40 shrink-0">
          <Select
            :model-value="button.type"
            :options="typeOptions"
            @update:model-value="updateButton(index, 'type', $event)"
          />
        </div>
        <div class="flex-1 min-w-0">
          <Input
            :model-value="button.title"
            :placeholder="
              t('MESSAGE_TEMPLATES.INSTAGRAM.BUTTONS.TITLE_PLACEHOLDER')
            "
            @update:model-value="updateButton(index, 'title', $event)"
          />
        </div>
        <Button
          v-if="canRemove"
          icon="i-lucide-x"
          variant="ghost"
          color="slate"
          size="sm"
          @click="removeButton(index)"
        />
      </div>

      <Input
        v-if="button.type === 'web_url'"
        :model-value="button.url"
        placeholder="https://"
        @update:model-value="updateButton(index, 'url', $event)"
      />
      <Input
        v-else
        :model-value="button.payload"
        :placeholder="
          t('MESSAGE_TEMPLATES.INSTAGRAM.BUTTONS.PAYLOAD_PLACEHOLDER')
        "
        :message="t('MESSAGE_TEMPLATES.INSTAGRAM.BUTTONS.PAYLOAD_HINT')"
        @update:model-value="updateButton(index, 'payload', $event)"
      />
    </div>

    <Button
      v-if="canAdd"
      icon="i-lucide-plus"
      color="slate"
      variant="outline"
      size="sm"
      :label="t('MESSAGE_TEMPLATES.INSTAGRAM.BUTTONS.ADD')"
      @click="addButton"
    />
  </div>
</template>
